export type PlanningMode = "simple" | "detailed";
export type TaskStatus = "not_started" | "in_progress" | "waiting_on_someone" | "blocked" | "done" | "skipped";
export type TaskPriority = "low" | "medium" | "high";
export type RsvpStatus = "waiting" | "yes" | "no" | "maybe";
export type GuestSide = "partner_1" | "partner_2" | "shared" | "other";
export type InvitationTier = "must_invite" | "nice_to_invite" | "b_list";
export type BudgetStage = "idea" | "researching" | "quote_received" | "deposit_paid" | "booked" | "paid_in_full" | "cancelled";
export type VendorStage =
  | "not_contacted"
  | "inquiry_sent"
  | "date_available"
  | "call_scheduled"
  | "quote_received"
  | "waiting_on_proposal"
  | "proposal_received"
  | "contract_signed"
  | "deposit_paid"
  | "booked"
  | "cut_budget"
  | "cut_vibe"
  | "cut_unavailable"
  | "cut_other";
export type ScheduleType = "weekend" | "morning" | "run_of_show";
export type VendorCategorySource = "preset" | "custom";

export interface WeddingPlanner {
  id: string;
  owner_user_id: string;
  partner_user_id?: string | null;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface WeddingPlannerProfile {
  id: string;
  planner_id: string;
  partner1_name?: string | null;
  partner2_name?: string | null;
  wedding_date?: string | null;
  wedding_location?: string | null;
  approximate_budget?: number | null;
  expected_guest_count?: number | null;
  currency: string;
  wedding_style?: string | null;
  planning_mode: PlanningMode;
  venue_booked: boolean;
  guest_list_started: boolean;
  created_at: string;
  updated_at: string;
}

export interface WeddingPriority {
  id: string;
  planner_id: string;
  priority_text: string;
  priority_type: "top_priority" | "not_important" | "dealbreaker" | "fallback_plan" | "private_note";
  visibility: "private_to_couple" | "visible_to_planner" | "visible_to_vendor" | "visible_to_guests";
  created_at: string;
  updated_at: string;
}

export interface PlanningTask {
  id: string;
  planner_id: string;
  title: string;
  description?: string | null;
  phase?: string | null;
  due_date?: string | null;
  assigned_to?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  notes?: string | null;
  system_generated: boolean;
  created_at: string;
  updated_at: string;
}

export interface WeddingGuest {
  id: string;
  planner_id: string;
  full_name: string;
  group_name?: string | null;
  category?: string | null;
  side?: GuestSide | null;
  relationship?: string | null;
  invitation_tier?: InvitationTier | null;
  invited_quantity: number;
  rsvp_status: RsvpStatus;
  confirmed_quantity?: number | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  meal_choice?: string | null;
  dietary_restrictions?: string[] | null;
  accommodation_needed?: boolean;
  hotel_block_status?: string | null;
  shuttle_needed?: boolean;
  table_number?: string | null;
  thank_you_sent: boolean;
  private_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetItem {
  id: string;
  planner_id: string;
  category: string;
  item: string;
  stage: BudgetStage;
  budgeted_amount?: number | null;
  vendor_estimate?: number | null;
  actual_amount?: number | null;
  vendor_name?: string | null;
  vendor_id?: string | null;
  contact_link?: string | null;
  deposit_amount?: number | null;
  payment_due_date?: string | null;
  contract_link?: string | null;
  payment_status?: "not_due" | "due_soon" | "overdue" | "paid" | "unknown" | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WeddingVendorCategory {
  id: string;
  planner_id: string;
  group_key: string;
  category_key: string;
  label: string;
  source: VendorCategorySource;
  is_enabled: boolean;
  is_complete: boolean;
  sort_order: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WeddingVendor {
  id: string;
  planner_id: string;
  category_id?: string | null;
  vendor_type: string;
  name: string;
  website?: string | null;
  stage_of_communication: VendorStage;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  quote?: number | null;
  estimated_total?: number | null;
  deposit_amount?: number | null;
  payment_due_date?: string | null;
  included_services?: string | null;
  extra_fees?: string | null;
  rating?: number | null;
  pros?: string[] | null;
  cons?: string[] | null;
  is_selected?: boolean | null;
  contract_status?: string | null;
  payment_status?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleItem {
  id: string;
  planner_id: string;
  schedule_type: ScheduleType;
  day?: string | null;
  time?: string | null;
  end_time?: string | null;
  action: string;
  location?: string | null;
  details?: string | null;
  owner?: string | null;
  coordinator_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlannerEntitlementStatus {
  hasAccess: boolean;
  entitlement?: string | null;
  plan?: string | null;
  status?: string | null;
}

export interface PlannerBundle {
  planner: WeddingPlanner;
  profile: WeddingPlannerProfile | null;
  priorities: WeddingPriority[];
  tasks: PlanningTask[];
  guests: WeddingGuest[];
  budgetItems: BudgetItem[];
  vendorCategories: WeddingVendorCategory[];
  vendors: WeddingVendor[];
  scheduleItems: ScheduleItem[];
  entitlement: PlannerEntitlementStatus;
}

export interface PlannerOnboardingInput {
  partner1Name: string;
  partner2Name: string;
  locale?: string;
  weddingDate?: string;
  weddingLocation?: string;
  expectedGuestCount?: number | null;
  approximateBudget?: number | null;
  currency: string;
  weddingStyle?: string;
  topPriorities: string;
  notImportant: string;
  venueBooked: boolean;
  guestListStarted: boolean;
  planningMode: PlanningMode;
}

export const PLANNER_ENTITLEMENT = "wedding_planner_access";

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const isoDate = (date: Date) => date.toISOString().slice(0, 10);

export function splitPriorityText(value: string): string[] {
  return value
    .split(/\n|,/)
    .map((entry) => entry.trim())
    .filter(Boolean)
    .slice(0, 10);
}

export function getDaysUntilWedding(profile?: WeddingPlannerProfile | null): number | null {
  if (!profile?.wedding_date) return null;
  const today = new Date();
  const weddingDate = new Date(`${profile.wedding_date}T12:00:00`);
  if (Number.isNaN(weddingDate.getTime())) return null;
  const diff = weddingDate.getTime() - today.getTime();
  return Math.ceil(diff / 86400000);
}

type ChecklistLocale = "en" | "hr";
type StarterTaskDoneWhen = "venueBooked" | "guestListStarted" | "approximateBudget" | "weddingDate";

type StarterChecklistTask = {
  title: Record<ChecklistLocale, string>;
  priority?: TaskPriority;
  doneWhen?: StarterTaskDoneWhen;
};

type StarterChecklistGroup = {
  key: string;
  label: Record<ChecklistLocale, string>;
  daysBefore: number;
  tasks: StarterChecklistTask[];
};

const text = (en: string, hr: string) => ({ en, hr });

const STARTER_CHECKLIST_GROUPS: StarterChecklistGroup[] = [
  {
    key: "12_months_out",
    label: text("12 Months Out", "12 mjeseci prije"),
    daysBefore: 365,
    tasks: [
      { title: text("Choose wedding motto", "Odaberite moto vjencanja") },
      { title: text("Plan engagement party", "Isplanirajte zarucnicku zabavu"), priority: "low" },
      { title: text("Establish a budget", "Postavite budzet"), priority: "high", doneWhen: "approximateBudget" },
      { title: text("Draft first guest list", "Slozite prvu listu gostiju"), priority: "high", doneWhen: "guestListStarted" },
      { title: text("Choose a time of year", "Odaberite dio godine") },
      { title: text("Choose a date", "Odaberite datum"), priority: "high", doneWhen: "weddingDate" },
      { title: text("Choose a vibe", "Odaberite stil i atmosferu") },
      { title: text("Choose a venue", "Odaberite lokaciju"), priority: "high", doneWhen: "venueBooked" },
      { title: text("Choose a caterer", "Odaberite catering"), priority: "high" },
      { title: text("Insure the engagement ring", "Osigurajte zarucnicki prsten"), priority: "low" },
    ],
  },
  {
    key: "11_months_out",
    label: text("11 Months Out", "11 mjeseci prije"),
    daysBefore: 335,
    tasks: [
      { title: text("Finalize design vision", "Finalizirajte vizualni smjer") },
      { title: text("Book DJ", "Rezervirajte DJ-a") },
      { title: text("Book photographer", "Rezervirajte fotografa"), priority: "high" },
      { title: text("Book band", "Rezervirajte bend"), priority: "low" },
      { title: text("Book videographer", "Rezervirajte videografa") },
      { title: text("Book florist", "Rezervirajte cvjecara") },
      { title: text("Book caterer / bartenders", "Rezervirajte catering / barmene"), priority: "high" },
      { title: text("Interview coordinators", "Razgovarajte s koordinatorima") },
      { title: text("Begin collecting addresses", "Pocnite skupljati adrese") },
      { title: text("Reserve hotel room block", "Rezervirajte hotelski blok soba"), priority: "low" },
    ],
  },
  {
    key: "10_months_out",
    label: text("10 Months Out", "10 mjeseci prije"),
    daysBefore: 305,
    tasks: [
      { title: text("Start dress shopping", "Krenite u potragu za haljinom") },
      { title: text("Take engagement photos", "Dogovorite zarucnicko fotografiranje"), priority: "low" },
      { title: text("Create wedding website", "Napravite web stranicu vjencanja"), priority: "low" },
      { title: text("Choose invitations", "Odaberite pozivnice") },
      { title: text("Book transportation", "Rezervirajte prijevoz") },
    ],
  },
  {
    key: "9_months_out",
    label: text("9 Months Out", "9 mjeseci prije"),
    daysBefore: 275,
    tasks: [
      { title: text("Buy wedding dress", "Kupite vjencanicu") },
      { title: text("Schedule alterations", "Dogovorite prepravke") },
      { title: text("Send save the dates", "Posaljite save-the-date obavijesti") },
      { title: text("Book or ask about officiant", "Rezervirajte ili pitajte maticara / celebranta"), priority: "high" },
      { title: text("Schedule hair and makeup trials", "Dogovorite probu frizure i sminke") },
    ],
  },
  {
    key: "8_months_out",
    label: text("8 Months Out", "8 mjeseci prije"),
    daysBefore: 245,
    tasks: [
      { title: text("Create registry", "Slozite listu poklona"), priority: "low" },
      { title: text("Select wedding party outfits", "Odaberite outfite za svadbenu pratnju") },
    ],
  },
  {
    key: "7_months_out",
    label: text("7 Months Out", "7 mjeseci prije"),
    daysBefore: 215,
    tasks: [
      { title: text("Book rehearsal dinner space", "Rezervirajte prostor za probnu veceru"), priority: "low" },
      { title: text("Hire ceremony musicians", "Angazirajte glazbenike za ceremoniju"), priority: "low" },
      { title: text("Order rentals", "Narucite najam opreme") },
      { title: text("Ask friends to be in the wedding party", "Pozovite prijatelje u svadbenu pratnju") },
    ],
  },
  {
    key: "6_months_out",
    label: text("6 Months Out", "6 mjeseci prije"),
    daysBefore: 185,
    tasks: [
      { title: text("Schedule time together", "Rezervirajte vrijeme samo za vas") },
      { title: text("Finalize decor plan", "Finalizirajte dekoracije") },
      { title: text("Finalize lighting plan", "Finalizirajte plan rasvjete") },
      { title: text("Begin premarital counseling", "Pocnite predbračno savjetovanje"), priority: "low" },
      { title: text("Discuss prenup if needed", "Razgovarajte o predbračnom ugovoru ako treba"), priority: "low" },
      { title: text("Make DIY plan", "Slozite DIY plan"), priority: "low" },
      { title: text("Schedule dance lessons", "Dogovorite satove plesa"), priority: "low" },
      { title: text("Try on suits", "Isprobajte odijela") },
    ],
  },
  {
    key: "5_months_out",
    label: text("5 Months Out", "5 mjeseci prije"),
    daysBefore: 155,
    tasks: [
      { title: text("Book guest transportation", "Rezervirajte prijevoz za goste") },
      { title: text("Book special transportation", "Rezervirajte poseban prijevoz") },
      { title: text("Book honeymoon", "Rezervirajte medeni mjesec"), priority: "low" },
    ],
  },
  {
    key: "4_months_out",
    label: text("4 Months Out", "4 mjeseca prije"),
    daysBefore: 125,
    tasks: [
      { title: text("Final tasting with caterer", "Finalna degustacija s cateringom"), priority: "high" },
      { title: text("Choose cake or dessert", "Odaberite tortu ili desert") },
      { title: text("Purchase wedding bands", "Kupite vjencane prstene"), priority: "high" },
      { title: text("Groomsmen suit try-on", "Isprobavanje odijela za kumove") },
      { title: text("Hair and makeup trial", "Proba frizure i sminke") },
      { title: text("Order invitations", "Narucite pozivnice") },
      { title: text("Finalize wedding coordinator", "Finalizirajte koordinatora vjencanja") },
    ],
  },
  {
    key: "3_months_out",
    label: text("3 Months Out", "3 mjeseca prije"),
    daysBefore: 95,
    tasks: [
      { title: text("Plan food options", "Dogovorite opcije hrane") },
      { title: text("Choose guest takeaways", "Odaberite poklone za goste"), priority: "low" },
      { title: text("Begin drafting vows", "Pocnite pisati zavjete") },
      { title: text("Select readings", "Odaberite citanja") },
      { title: text("Finish DIY projects", "Dovrsite DIY projekte"), priority: "low" },
      { title: text("Confirm odds and ends list", "Potvrdite listu sitnica") },
      { title: text("Send invitations", "Posaljite pozivnice"), priority: "high" },
      { title: text("Create rain or crisis plan", "Slozite plan za kisu ili krizne situacije"), priority: "high" },
    ],
  },
  {
    key: "2_months_out",
    label: text("2 Months Out", "2 mjeseca prije"),
    daysBefore: 65,
    tasks: [
      { title: text("Set RSVP deadline six weeks before wedding", "Postavite RSVP rok sest tjedana prije vjencanja"), priority: "high" },
      { title: text("Send rehearsal dinner invites", "Posaljite pozivnice za probnu veceru"), priority: "low" },
      { title: text("Dress fitting", "Proba vjencanice") },
      { title: text("Select wedding undergarments", "Odaberite donje rublje za vjencanje") },
      { title: text("Get marriage license", "Ishodite dokumente za vjencanje"), priority: "high" },
      { title: text("Buy wedding party gifts", "Kupite poklone za svadbenu pratnju") },
      { title: text("Review floral mock-up", "Pregledajte cvjetni mock-up") },
      { title: text("Share song lists with music team", "Predajte liste pjesama glazbenom timu") },
      { title: text("Purchase small items", "Kupite sitnice") },
      { title: text("Host bridal shower", "Organizirajte bridal shower"), priority: "low" },
      { title: text("Host bachelor / bachelorette", "Organizirajte momacku / djevojacku"), priority: "low" },
      { title: text("Make shot list with photographer", "Slozite shot listu s fotografom") },
      { title: text("Finalize day-of schedule", "Finalizirajte raspored dana"), priority: "high" },
      { title: text("Confirm vendors", "Potvrdite dobavljace"), priority: "high" },
      { title: text("Order thank-you cards", "Narucite zahvalnice"), priority: "low" },
    ],
  },
  {
    key: "month_of",
    label: text("Month Of", "Mjesec vjencanja"),
    daysBefore: 30,
    tasks: [
      { title: text("Pay vendors in full", "Platite dobavljace u cijelosti"), priority: "high" },
      { title: text("Create seating chart and signage", "Slozite raspored sjedenja i natpise"), priority: "high" },
      { title: text("Prepare escort cards", "Pripremite escort kartice") },
      { title: text("Final venue walkthrough", "Zavrsni obilazak lokacije"), priority: "high" },
      { title: text("Prepare vendor tips", "Pripremite napojnice za dobavljace"), priority: "low" },
      { title: text("Break in wedding shoes", "Razgazite vjencane cipele") },
      { title: text("Chase missing RSVPs", "Podsjetite goste koji nisu odgovorili"), priority: "high" },
      { title: text("Send final numbers to vendors", "Posaljite konacne brojke dobavljacima"), priority: "high" },
      { title: text("Finish online purchases", "Dovrsite online kupnje") },
      { title: text("Collect memorial table photos", "Prikupite fotografije za memorijalni stol"), priority: "low" },
      { title: text("Make playlists", "Slozite playliste") },
      { title: text("Finalize ceremony with officiant", "Finalizirajte ceremoniju s maticarom / celebrantom"), priority: "high" },
    ],
  },
  {
    key: "week_of",
    label: text("Week Of", "Tjedan vjencanja"),
    daysBefore: 7,
    tasks: [
      { title: text("Nail appointment", "Termin za nokte"), priority: "low" },
      { title: text("Rest and reset", "Odmorite se") },
      { title: text("Final dress fitting", "Zadnja proba vjencanice") },
      { title: text("Pack for honeymoon", "Spakirajte se za medeni mjesec"), priority: "low" },
      { title: text("Clean rings", "Ocistite prstenje") },
      { title: text("Practice vows", "Vjezbajte zavjete") },
      { title: text("Write notes to each other", "Napisite pisma jedno drugome"), priority: "low" },
      { title: text("Assemble bathroom baskets and final decor", "Slozite kosarice za toalet i finalni dekor") },
    ],
  },
  {
    key: "night_before",
    label: text("Night Before", "Vecer prije"),
    daysBefore: 1,
    tasks: [
      { title: text("Pack the car", "Spakirajte auto"), priority: "high" },
      { title: text("Get a good night's sleep", "Dobro se naspavajte"), priority: "high" },
      { title: text("Watch a silly movie and relax", "Pogledajte glupi film i opustite se"), priority: "low" },
    ],
  },
];

const normalizeChecklistLocale = (locale: string | null | undefined): ChecklistLocale =>
  String(locale ?? "").toLowerCase().startsWith("hr") ? "hr" : "en";

const isDoneByInput = (doneWhen: StarterTaskDoneWhen | undefined, input: PlannerOnboardingInput) => {
  if (doneWhen === "venueBooked") return input.venueBooked;
  if (doneWhen === "guestListStarted") return input.guestListStarted;
  if (doneWhen === "approximateBudget") return Number(input.approximateBudget ?? 0) > 0;
  if (doneWhen === "weddingDate") return Boolean(input.weddingDate);
  return false;
};

export function buildInitialTasks(input: PlannerOnboardingInput, plannerId: string): Array<Omit<PlanningTask, "id" | "created_at" | "updated_at">> {
  const weddingDate = input.weddingDate ? new Date(`${input.weddingDate}T12:00:00`) : null;
  const hasDate = weddingDate && !Number.isNaN(weddingDate.getTime());
  const relativeDate = (daysBefore: number) => hasDate ? isoDate(addDays(weddingDate, -daysBefore)) : null;
  const locale = normalizeChecklistLocale(input.locale);

  return STARTER_CHECKLIST_GROUPS.flatMap((group) =>
    group.tasks.map((task, index) => {
      const spreadDaysBefore = Math.max(group.daysBefore - Math.min(index, 20), 1);
      return {
        planner_id: plannerId,
        title: task.title[locale],
        description: null,
        phase: group.label[locale],
        due_date: relativeDate(spreadDaysBefore),
        assigned_to: null,
        status: isDoneByInput(task.doneWhen, input) ? "done" : "not_started",
        priority: task.priority ?? "medium",
        notes: null,
        system_generated: true,
      };
    }),
  );
}

export function getBudgetSummary(profile: WeddingPlannerProfile | null, items: BudgetItem[]) {
  const plannedBudget = profile?.approximate_budget ?? 0;
  const estimated = items.reduce((sum, item) => sum + Number(item.vendor_estimate ?? item.budgeted_amount ?? 0), 0);
  const actual = items.reduce((sum, item) => sum + Number(item.actual_amount ?? 0), 0);
  const committed = actual || estimated;
  const remaining = plannedBudget ? plannedBudget - committed : null;
  return { plannedBudget, estimated, actual, committed, remaining };
}

export function getGuestSummary(guests: WeddingGuest[]) {
  return guests.reduce((summary, guest) => {
    summary.invited += Number(guest.invited_quantity || 0);
    if (guest.rsvp_status === "yes") summary.yes += Number(guest.confirmed_quantity ?? guest.invited_quantity ?? 0);
    if (guest.rsvp_status === "no") summary.no += Number(guest.invited_quantity || 0);
    if (guest.rsvp_status === "maybe") summary.maybe += Number(guest.invited_quantity || 0);
    if (guest.rsvp_status === "waiting") summary.waiting += Number(guest.invited_quantity || 0);
    if (guest.rsvp_status === "yes" && !guest.table_number) summary.unseated += Number(guest.confirmed_quantity ?? guest.invited_quantity ?? 0);
    return summary;
  }, { invited: 0, yes: 0, no: 0, maybe: 0, waiting: 0, unseated: 0 });
}

export function getPlanningProgress(tasks: PlanningTask[]) {
  if (tasks.length === 0) return 0;
  const done = tasks.filter((task) => task.status === "done" || task.status === "skipped").length;
  return Math.round((done / tasks.length) * 100);
}

export function getOverdueTasks(tasks: PlanningTask[]) {
  const today = isoDate(new Date());
  return tasks.filter((task) => task.due_date && task.due_date < today && task.status !== "done" && task.status !== "skipped");
}

export function getUpcomingPayments(items: BudgetItem[]) {
  const today = new Date();
  const soon = addDays(today, 30);
  return items
    .filter((item) => {
      if (!item.payment_due_date || item.payment_status === "paid") return false;
      const due = new Date(`${item.payment_due_date}T12:00:00`);
      return due <= soon;
    })
    .sort((a, b) => String(a.payment_due_date).localeCompare(String(b.payment_due_date)))
    .slice(0, 5);
}

export function detectPlannerRisks(bundle: Pick<PlannerBundle, "profile" | "tasks" | "guests" | "budgetItems" | "vendors" | "scheduleItems">): string[] {
  const risks: string[] = [];
  const days = getDaysUntilWedding(bundle.profile);
  const guestSummary = getGuestSummary(bundle.guests);
  const budget = getBudgetSummary(bundle.profile, bundle.budgetItems);

  if (bundle.profile?.wedding_date && !bundle.profile.venue_booked) {
    risks.push("Wedding date exists but the venue is not marked as booked.");
  }
  if ((bundle.profile?.expected_guest_count ?? 0) > 0 && guestSummary.invited > (bundle.profile?.expected_guest_count ?? 0)) {
    risks.push("Invited guest count is above the planning estimate.");
  }
  if (budget.plannedBudget > 0 && budget.committed > budget.plannedBudget) {
    risks.push("Current budget items exceed the planned budget.");
  }
  if (getOverdueTasks(bundle.tasks).length > 0) {
    risks.push("There are overdue checklist items.");
  }
  if (days !== null && days < 45 && bundle.scheduleItems.length === 0) {
    risks.push("Wedding is close and no run-of-show items exist yet.");
  }
  if (days !== null && days < 60 && guestSummary.waiting > guestSummary.invited * 0.25) {
    risks.push("Many RSVPs are still waiting close to the wedding date.");
  }
  if (bundle.vendors.some((vendor) => vendor.stage_of_communication === "booked" && !vendor.contract_status)) {
    risks.push("A booked vendor is missing contract status.");
  }
  if (bundle.budgetItems.some((item) => !item.vendor_estimate && !item.actual_amount)) {
    risks.push("Some budget categories have no estimate or actual cost.");
  }

  return risks.slice(0, 6);
}

export function getNextRecommendedTasks(tasks: PlanningTask[]) {
  return [...tasks]
    .filter((task) => task.status !== "done" && task.status !== "skipped")
    .sort((a, b) => {
      const priorityScore = { high: 0, medium: 1, low: 2 } as const;
      const byDate = String(a.due_date ?? "9999").localeCompare(String(b.due_date ?? "9999"));
      return byDate || priorityScore[a.priority] - priorityScore[b.priority];
    })
    .slice(0, 7);
}
